import { inject, Injectable } from '@angular/core';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { Mapper } from '@shared/helpers/mapper';
import { HeaderService } from '@shared/services';
import { QueryOptions } from 'odata-query';
import { map, Observable, Subject } from 'rxjs';
import {
  GstarClient,
  TaxSummaryHeaderTypeResponseODataWithMetadataResponse,
  TaxSummaryWithNestedObjectsResponseODataWithMetadataResponse,
  UpdateTaxSummaryRequest,
} from 'src/app/web-api-client';
import { ActiveTaxSummary } from '../abstractions/active-tax-summary';
import { TaxSummaryDetail } from '../models/tax-summary-detail';
import { TaxSummaryHeader } from '../models/tax-summary-header';
import { TaxSummaryType } from '../models/tax-summary-type';

type TaxSummaryResponse =
  TaxSummaryWithNestedObjectsResponseODataWithMetadataResponse;
type TaxSummaryRequestQuery = Partial<
  QueryOptions<TaxSummaryWithNestedObjectsResponseODataWithMetadataResponse>
>;

@Injectable()
export class TaxSummaryService {
  private readonly gstarClient = inject(GstarClient);
  private readonly headerService = inject(HeaderService);

  activeTaxSummary$: Subject<ActiveTaxSummary> = new Subject();

  getTaxSummariesTypesHeaders(): Observable<TaxSummaryHeader[]> {
    return this.gstarClient
      .taxsummaryheadertypeGET(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      )
      .pipe(
        map(
          (
            response: TaxSummaryHeaderTypeResponseODataWithMetadataResponse
          ): TaxSummaryHeader[] => this.parseToTaxSummaryHeader(response)
        )
      );
  }

  getTaxSummaryByHeaderAndCountry(
    taxSummaryHeaderTypeId: number,
    countryId: number
  ): Observable<TaxSummaryDetail[]> {
    return this.taxSummaryQuery({
      filter: {
        taxSummaryType: {
          taxSummaryHeaderType: { taxSummaryHeaderTypeId },
        },
        countryId,
      },
    });
  }

  taxSummaryById(
    taxSummaryId: number
  ): Observable<TaxSummaryDetail | undefined> {
    return this.taxSummaryQuery({ filter: { taxSummaryId } }).pipe(
      map((taxSummaryDetailList: TaxSummaryDetail[]) =>
        taxSummaryDetailList.at(0)
      )
    );
  }

  updateTaxSummary(
    taxSummaryDetail: TaxSummaryDetail,
    html: string,
    reviewDate?: Date
  ): Observable<void> {
    const req = this.getUpdateTaxSummaryRequest(
      taxSummaryDetail,
      html,
      reviewDate
    );
    return this.gstarClient.taxsummaryPUT(req.taxSummaryId, req);
  }

  private getUpdateTaxSummaryRequest(
    {
      countryId,
      isActive,
      isApproved,
      taxSummaryId,
      taxSummaryType,
    }: TaxSummaryDetail,
    html: string,
    reviewDate?: Date
  ): UpdateTaxSummaryRequest {
    return Mapper.map({}, UpdateTaxSummaryRequest, [
      {
        description: html,
        countryId,
        isActive,
        isApproved,
        taxSummaryId,
        taxSummaryTypeId: taxSummaryType.taxSummaryTypeId,
        reviewDate,
      },
    ]);
  }

  private taxSummaryQuery(
    query: TaxSummaryRequestQuery
  ): Observable<TaxSummaryDetail[]> {
    return this.gstarClient
      .byclient4(
        this.headerService.selectedClient()?.id,
        undefined,
        buildQueryFilter(query),
        undefined,
        undefined,
        undefined,
        'true',
        undefined
      )
      .pipe(
        map((queryResponse: TaxSummaryResponse): TaxSummaryDetail[] => {
          return this.parseTaxSummaryDetailList(queryResponse);
        })
      );
  }

  private parseToTaxSummaryHeader(
    response: TaxSummaryHeaderTypeResponseODataWithMetadataResponse
  ): TaxSummaryHeader[] {
    if (response && response.value && response.value?.length > 0) {
      return Mapper.mapByList(response.value, TaxSummaryHeader, [
        {
          taxSummaryHeaderId: source => source.taxSummaryHeaderTypeId,
          name: source => source.name?.toLowerCase(),
        },
      ]);
    }
    return [];
  }

  private parseTaxSummaryDetailList(
    response: TaxSummaryResponse
  ): TaxSummaryDetail[] {
    if (response.value && response.value?.length > 0) {
      return Mapper.mapByList(response.value, TaxSummaryDetail, [
        {
          taxSummaryId: (source): number => source.taxSummaryId!,
          isActive: (source): boolean => source.isActive!,
          isApproved: (source): boolean => source.isApproved!,
          countryId: (source): number => source.countryId!,
          description: (source): string => source.description!,
          header: (source): TaxSummaryType =>
            Mapper.map(
              source.taxSummaryType!.taxSummaryHeaderType!,
              TaxSummaryType,
              [
                {
                  taxSummaryTypeId: (source): number =>
                    source!.taxSummaryHeaderTypeId!,
                  name: (source): string => source.name!,
                },
              ]
            ),
          taxSummaryType: (source): TaxSummaryType =>
            Mapper.map(source.taxSummaryType, TaxSummaryType, [
              {
                taxSummaryTypeId: (source): number => source!.taxSummaryTypeId!,
                name: (source): string => source!.name!,
              },
            ]),
          reviewDate: (source): Date | undefined => source.reviewDate,
        },
      ]);
    }
    return [];
  }
}
